/*
 * @Author: 刘洪壮 liuhongzhuang@nextmar.com
 * @Date: 2025-04-18 13:13:01
 * @LastEditors: 刘洪壮 liuhongzhuang@nextmar.com
 * @LastEditTime: 2025-05-14 11:39:07
 * @FilePath: /zeelool-nextjs/src/components/ImagePreview/index.tsx
 * @Description: 图片预览组件
 */
import Image from '@/components/Image';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { Box, BoxProps, CircularProgress } from '@mui/material';
import {
  CloseOutlined,
  DeleteOutlined,
  FangDaOutLined,
  LeftArrowOutlined,
  RightArrowOutlined,
} from '@zeelool/icon';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { PhotoSlider } from 'react-photo-view';
import FullIcon from './assets/full.svg';
import ZoomDownIcon from './assets/zoom-down.svg';
import ZoomUpIcon from './assets/zoom-up.svg';
export interface ImagePreviewRef {
  handleVisibleChange: (index: number) => void;
}

export interface ImagePreviewProps extends BoxProps {
  /**
   * 图片URL列表
   */
  images: string[];
  /**
   * 缩略图宽度
   */
  width?: number;
  /**
   * 缩略图高度
   */
  height?: number;
  /**
   * 缩略图间距
   */
  gap?: number;
  /**
   * 图片域名前缀，默认为空
   */
  domainPrefix?: string;
  /**
   * 是否显示缩略图
   */
  showThumbnail?: boolean;

  /**
   * 是否显示允许删除
   */
  allowDelete?: boolean;

  /**
   * 删除图片的回调
   */
  onDelete?: (e: React.MouseEvent) => void;
  /**
   * 蒙层透明度
   */
  maskOpacity?: number;
}

const ImagePreview = forwardRef<ImagePreviewRef, ImagePreviewProps>(
  (
    {
      images = [],
      width = 80,
      height = 80,
      gap = 8,
      domainPrefix = '',
      sx,
      showThumbnail = true,
      allowDelete = false,
      onDelete,
      maskOpacity = 0.8,
      ...boxProps
    },
    ref,
  ) => {
    const [index, setIndex] = useState(0);
    const [visible, setVisible] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const setZoomDisablerType = useStore((state) => state.setZoomDisablerType);

    const handleVisibleChange = (_index: number) => {
      setVisible(true);
      setIndex(_index);
    };

    useImperativeHandle(ref, () => ({
      handleVisibleChange,
    }));

    const handleIndexChange = (type: 'prev' | 'next') => {
      if (type === 'prev' && index > 0) {
        setIndex(index - 1);
      } else if (type === 'next' && index < images.length - 1) {
        setIndex(index + 1);
      }
    };

    const handleMouseEnter = useCallback((_index: number) => {
      setIsHovering(true);
      setIndex(_index);
    }, []);

    const handleMouseLeave = useCallback((_index: number) => {
      setIsHovering(false);
      setIndex(_index);
    }, []);

    useEffect(() => {
      setZoomDisablerType({
        zoom: !visible,
        wheelZoom: !visible,
        keyboardZoom: !visible,
        doubleTapZoom: !visible,
        contextMenu: !visible,
      });
      return () => setZoomDisablerType({});
    }, [visible, setZoomDisablerType]);

    // 如果没有图片，不显示任何内容

    if (images.length === 0) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap,
          ...sx,
        }}
        {...boxProps}
      >
        {showThumbnail &&
          images.map((image, _index) => {
            const fullImageUrl = `${domainPrefix}${image}`;
            return (
              <Box
                key={fullImageUrl}
                className="cursor-pointer"
                onClick={() => handleVisibleChange(_index)}
                onMouseEnter={() => handleMouseEnter(_index)}
                onMouseLeave={() => handleMouseLeave(_index)}
                sx={{
                  position: 'relative',
                  width,
                  height,
                  overflow: 'hidden',
                  borderRadius: '4px',
                }}
              >
                <Image
                  src={fullImageUrl}
                  alt={`image ${index + 1}`}
                  width={width}
                  height={height}
                  className="object-cover w-full h-full"
                />

                {isHovering && _index === index && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center gap-2 rounded-6">
                    <FangDaOutLined className="text-white" size={16} />
                    {allowDelete && (
                      <DeleteOutlined className="text-white" size={16} onClick={onDelete} />
                    )}
                  </div>
                )}
              </Box>
            );
          })}

        <PhotoSlider
          maskOpacity={maskOpacity}
          images={images.map((item) => ({ src: item, key: item }))}
          visible={visible}
          onClose={() => {
            setVisible(false);
          }}
          index={index}
          onIndexChange={setIndex}
          loadingElement={
            <Box className="w-full h-full flex items-center justify-center">
              <CircularProgress />
            </Box>
          }
          bannerVisible={false}
          overlayRender={({ onScale, scale }) => {
            return (
              <>
                <div
                  className="absolute top-[40px] right-[20px] w-[40px] h-[40px] flex items-center justify-center cursor-pointer z-999  rounded-20 bg-[rgba(0,0,0,0.4)]"
                  onClick={() => {
                    setVisible(false);
                  }}
                >
                  <CloseOutlined className="text-white" />
                </div>

                <WapOnly>
                  {/* 底部显示栏 */}
                  <div className="absolute bottom-[40px] left-[50%] translate-x-[-50%] w-[55px] h-[24px] bg-[rgba(0,0,0,0.4)] rounded-[24px] text-white py-4 text-13 flex items-center justify-around cursor-pointer z-999 ">
                    {`${index + 1} / ${images.length}`}
                  </div>
                </WapOnly>

                <WebOnly>
                  {images.length > 1 && (
                    <>
                      <div
                        className="absolute top-[50%] left-[20px] w-[40px] h-[40px] bg-[rgba(0,0,0,0.4)] rounded-20 flex items-center justify-center cursor-pointer z-999 "
                        onClick={(e) => {
                          e.stopPropagation();
                          handleIndexChange('prev');
                        }}
                      >
                        <LeftArrowOutlined className="text-white" />
                      </div>
                      <div
                        className="absolute top-[50%] right-[20px] w-[40px] h-[40px] bg-[rgba(0,0,0,0.4)] rounded-20 flex items-center justify-center cursor-pointer z-999"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleIndexChange('next');
                        }}
                      >
                        <RightArrowOutlined className="text-white" />
                      </div>
                    </>
                  )}
                  <div className="absolute bottom-[20px] left-[50%] translate-x-[-50%] w-[180px] h-[48px] bg-[rgba(0,0,0,0.6)] rounded-[24px] flex items-center justify-around cursor-pointer z-999 ">
                    {/* 缩小 */}
                    <div onClick={() => onScale(scale - 0.5)}>
                      <Image src={ZoomDownIcon} alt="zoom-down" width={24} height={24} />
                    </div>
                    {/* 放大 */}
                    <div onClick={() => onScale(scale + 0.5)}>
                      <Image src={ZoomUpIcon} alt="zoom-up" width={24} height={24} />
                    </div>
                    {/* 全屏幕 */}
                    <div onClick={() => onScale(1)}>
                      <Image src={FullIcon} alt="full" width={24} height={24} />
                    </div>
                  </div>
                  <div
                    className="fixed inset-0 z-998"
                    style={{ background: 'transparent' }}
                    onClick={() => {
                      setVisible(false);
                    }}
                  />
                </WebOnly>
              </>
            );
          }}
        />
      </Box>
    );
  },
);

ImagePreview.displayName = 'ImagePreview';

export default ImagePreview;
